import { Controller } from "@hotwired/stimulus"
import { useStore } from "stimulus-store"
import { allUsersStore } from "../../stores/organization/all_users_store"
import { sendFailureNotification, sendSuccessNotification } from "shared/toast_notifications"
import { getCsrfToken } from "shared/form_helpers"

// Connects to data-controller="table--users-table"
export default class extends Controller {

    static stores = [
        allUsersStore
    ]

    static targets = [
        "table"
    ]

    static values = {
        currentUserRole: String,
        currentUserId: String
    }

    connect() {
        useStore(this)

        // Defer so argus--table connects first
        Promise.resolve().then(() => {
            this.initializeTable()
        })
    }

    /**
     * React to changes in the all users store
     */
    onAllUsersStoreUpdate() {
        this.updateTableData()
    }

    /**
     * Get the argus--table controller instance
     */
    _getArgusTable() {
        if (!this.hasTableTarget) return null
        return this.application.getControllerForElementAndIdentifier(
            this.tableTarget, 'argus--table'
        )
    }

    /**
     * Initialize the table via argus--table
     */
    initializeTable() {
        if (!this.hasTableTarget) {
            console.warn('table--users-table: table target not found')
            return
        }

        const argusTable = this._getArgusTable()
        if (!argusTable) {
            console.warn('table--users-table: argus--table controller not found')
            return
        }

        // Set up columns programmatically
        argusTable._columns = [
            { field: 'name', title: 'Name', sortable: true, visible: true, formatter: (value, row) => this.nameFormatter(value, row) },
            { field: 'email', title: 'Email', sortable: true, visible: true },
            { field: 'status', title: 'Status', sortable: true, visible: true, formatter: (value) => this.statusFormatter(value) },
            { field: 'last_sign_in_at', title: 'Last Activity', sortable: true, visible: true, formatter: (value) => this.dateFormatter(value) },
            { field: 'actions', title: 'Permissions', sortable: false, visible: true, formatter: (value, row) => this.permissionsButtonFormatter(value, row) }
        ]

        // Rebuild column toggler
        const ColumnTogglerClass = argusTable.columnToggler.constructor
        argusTable.columnToggler = new ColumnTogglerClass(
            argusTable._columns, () => argusTable._render()
        )

        // Controls are bound declaratively via Stimulus data-action, so a button
        // written into a patched row is live without a post-render re-attach step.

        // Load initial data
        const initialData = this.prepareTableData()
        argusTable.loadData(initialData)
    }

    /**
     * Prepare table data from all users store
     */
    prepareTableData() {
        const users = this.allUsersStoreValue || []
        return users.map(user => ({
            id: user.id,
            name: user.name || '---',
            email: user.email,
            admin: user.admin,
            status: user.status,
            last_sign_in_at: user.last_sign_in_at
        }))
    }

    /**
     * Update table data when store changes
     */
    updateTableData() {
        if (!this.hasTableTarget) return

        const argusTable = this._getArgusTable()
        if (!argusTable) return

        const newData = this.prepareTableData()
        argusTable.loadData(newData)
    }

    /**
     * Manually refresh the table
     */
    refresh() {
        this.updateTableData()
    }

    /**
     * Clear the table
     */
    clear() {
        const argusTable = this._getArgusTable()
        if (argusTable) argusTable.clearData()
    }

    /**
     * Get current table data
     */
    getData() {
        const argusTable = this._getArgusTable()
        return argusTable ? argusTable.getData() : []
    }

    /*
     * FORMATTERS
     */

    nameFormatter(value, row) {
        if (!value || value === '---') {
            return '<span class="text-muted">Pending Confirmation</span>'
        }
        return this.escapeHtml(value)
    }

    statusFormatter(value) {
        return this.escapeHtml(value)
    }

    dateFormatter(value) {
        if (!value) {
            return '<span class="text-muted">N/A</span>'
        }

        try {
            const date = new Date(value)
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            })
        } catch (error) {
            return '<span class="text-muted">Invalid Date</span>'
        }
    }

    permissionsButtonFormatter(value, row) {
        const isSuspended = row.status === 'Suspended'
        const suspendButtonText = isSuspended ? 'Unsuspend' : 'Suspend'
        const suspendAction = isSuspended ? 'unsuspend' : 'suspend'

        return `
            <button 
                type="button" 
                class="btn btn-sm btn-outline-primary permissions-btn me-2 my-1"
                data-user-id="${ row.id }"
                data-action="click->table--users-table#handlePermissionsButtonClick"
            >
                <i class="fas fa-user-shield"></i> Permissions
            </button>
            <button 
                type="button" 
                class="btn btn-sm btn-secondary suspend-btn my-1"
                data-user-id="${ row.id }"
                data-suspend-action="${ suspendAction }"
                data-user-email="${ this.escapeHtml(row.email) }"
                data-action="click->table--users-table#handleSuspendButtonClick"
            >
                <i class="fas fa-${ isSuspended ? 'user-check' : 'user-slash' }"></i> ${ suspendButtonText }
            </button>
        `
    }

    /**
     * Handle permissions button click
     */
    async handlePermissionsButtonClick(event) {
        const button = event.currentTarget
        const userId = button.dataset.userId

        if (!userId) {
            console.error('No user ID found on permissions button')
            return
        }

        const originalHTML = button.innerHTML
        button.disabled = true
        button.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span> Loading...'

        try {
            const permissionsModal = document.getElementById('permissionsModal')

            if (!permissionsModal) {
                console.error('Permissions modal not found')
                return
            }

            const permissionsFormController = this.application.getControllerForElementAndIdentifier(
                permissionsModal,
                'forms--permissions-form'
            )

            if (permissionsFormController) {
                await permissionsFormController.open({ params: { userId: userId } })
            } else {
                console.error('Permissions form controller not found')
            }
        } catch (error) {
            console.error('Error opening permissions modal:', error)
        } finally {
            button.disabled = false
            button.innerHTML = originalHTML
        }
    }

    /**
     * Handle suspend/unsuspend button click
     */
    async handleSuspendButtonClick(event) {
        const button = event.currentTarget
        const userId = button.dataset.userId
        const action = button.dataset.suspendAction
        const userEmail = button.dataset.userEmail

        if (!userId || !action) {
            console.error('Missing user ID or action on suspend button')
            return
        }

        const actionText = action === 'suspend' ? 'suspend' : 'unsuspend'
        const confirmMessage = `Are you sure you want to ${ actionText } ${ userEmail }?`

        if (!confirm(confirmMessage)) {
            return
        }

        const originalHTML = button.innerHTML
        button.disabled = true
        button.innerHTML = '<span class="spinner-border spinner-border-sm"></span>'

        // Track this native fetch on the shared window-level counter so wait_for_ajax
        // (which polls window.__pendingFetchRequests) can observe it. Without this the
        // fetch is invisible to the test harness, so a system spec chaining
        // suspend -> unsuspend -> suspend could read the DB while a request is still in
        // flight (or match a lingering prior success toast) and see the wrong state.
        this._incrementPendingRequests()

        try {
            const endpoint = action === 'suspend' ? '/api/user/suspend' : '/api/user/unsuspend'

            const response = await fetch(endpoint, {
                method: 'PATCH',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: JSON.stringify({ id: userId })
            })

            const responseBody = await response.json()

            if (!response.ok) {
                throw new Error(responseBody.message || responseBody.error || `Unable to ${ actionText } a user. Try again.`)
            }

            sendSuccessNotification(`${ responseBody.data.name } ${ actionText }ed successfully`)

            // Patch only this user's row in place so sibling rows keep their control
            // nodes. The resulting status comes from the server response (the same
            // value the store path would have propagated). We deliberately do NOT
            // set allUsersStore here: that would fire onAllUsersStoreUpdate ->
            // updateTableData -> loadData and rebuild the whole table. The sortable/
            // searchable `status` column relies on the shipped _affectsSort /
            // _affectsFilter fallback to full-render when needed.
            const argusTable = this._getArgusTable()
            if (argusTable) {
                argusTable.updateRow(userId, { status: responseBody.data.status })
            }

        } catch (error) {
            console.error(`Error ${ action }ing user:`, error)
            sendFailureNotification(`Error ${ action }ing user: ${ error.message }`)
        } finally {
            this._decrementPendingRequests()
            button.disabled = false
            button.innerHTML = originalHTML
        }
    }

    // Track in-flight fetch requests on a shared window-level counter so that
    // fetch-based async work is observable to the same tooling that watches
    // jQuery.active and Turbo visits (spec/support/wait_for_ajax.rb). The counter
    // is process-global (not per-controller) so a single idle check covers all
    // fetch activity.
    _incrementPendingRequests() {
        window.__pendingFetchRequests = (window.__pendingFetchRequests || 0) + 1
    }

    _decrementPendingRequests() {
        const next = (window.__pendingFetchRequests || 0) - 1
        window.__pendingFetchRequests = next > 0 ? next : 0
    }

    escapeHtml(text) {
        if (!text) return ''
        const div = document.createElement('div')
        div.textContent = text
        return div.innerHTML
    }
}
